import type {
  LocalWithdrawalRecord,
} from "../types/offlineWithdrawal";


const DATABASE_NAME =
  "materialentnahme";

const DATABASE_VERSION = 4;

const WITHDRAWALS_STORE =
  "withdrawals";

const RECOVERY_STORE =
  "withdrawal_recovery";

const WITHDRAWAL_NUMBER_STATE_STORE =
  "withdrawal_number_state";


let databasePromise:
  Promise<IDBDatabase> | null = null;


export class LocalWithdrawalRevisionConflictError extends Error {
  readonly clientUuid: string;
  readonly expectedRevision: number;
  readonly actualRevision: number;

  constructor(clientUuid: string, expectedRevision: number, actualRevision: number) {
    super(`Lokale Revision hat sich geändert (${expectedRevision} → ${actualRevision}).`);
    this.name = "LocalWithdrawalRevisionConflictError";
    this.clientUuid = clientUuid;
    this.expectedRevision = expectedRevision;
    this.actualRevision = actualRevision;
  }
}


function withPreservedActiveSyncLease(
  current: LocalWithdrawalRecord | undefined,
  next: LocalWithdrawalRecord,
): LocalWithdrawalRecord {
  const currentExpiry = current?.sync_lease_expires_at ? Date.parse(current.sync_lease_expires_at) : Number.NaN;
  const currentLeaseActive = Boolean(
    current?.sync_lease_owner
    && Number.isFinite(currentExpiry)
    && currentExpiry > Date.now()
  );
  const preserveForeignLease = currentLeaseActive
    && current?.sync_lease_owner !== next.sync_lease_owner;

  if (!preserveForeignLease) return next;
  return {
    ...next,
    sync_lease_owner: current?.sync_lease_owner ?? null,
    sync_lease_expires_at: current?.sync_lease_expires_at ?? null,
    sync_lease_updated_at: current?.sync_lease_updated_at ?? null,
  };
}


function openDatabase(): Promise<IDBDatabase> {
  if (databasePromise) {
    return databasePromise;
  }

  databasePromise =
    new Promise(
      (
        resolve,
        reject
      ) => {
        const request =
          indexedDB.open(
            DATABASE_NAME,
            DATABASE_VERSION
          );

        request.onerror = () => {
          databasePromise = null;
          reject(
            request.error
          );
        };

        request.onblocked = () => {
          console.warn("IndexedDB-Upgrade blockiert: Eine ältere Materialentnahme-Instanz hält die Datenbank noch geöffnet.");
        };

        request.onupgradeneeded =
          () => {
            const database =
              request.result;

            let store: IDBObjectStore;
            if (!database.objectStoreNames.contains(WITHDRAWALS_STORE)) {
              store = database.createObjectStore(WITHDRAWALS_STORE, { keyPath: "client_uuid" });
            } else {
              store = request.transaction!.objectStore(WITHDRAWALS_STORE);
            }

            if (!store.indexNames.contains("sync_status")) {
              store.createIndex("sync_status", "sync_status", { unique: false });
            }
            if (!store.indexNames.contains("created_at")) {
              store.createIndex("created_at", "created_at", { unique: false });
            }
            if (!store.indexNames.contains("owner_user_id")) {
              store.createIndex("owner_user_id", "owner_user_id", { unique: false });
            }

            if (!database.objectStoreNames.contains(RECOVERY_STORE)) {
              database.createObjectStore(RECOVERY_STORE, { keyPath: "client_uuid" });
            }

            if (!database.objectStoreNames.contains(WITHDRAWAL_NUMBER_STATE_STORE)) {
              database.createObjectStore(WITHDRAWAL_NUMBER_STATE_STORE, { keyPath: "owner_user_id" });
            }
          };

        request.onsuccess =
          () => {
            const database =
              request.result;

            database.onversionchange =
              () => {
                database.close();
                databasePromise = null;
              };

            resolve(database);
          };
      }
    );

  return databasePromise;
}


export interface LocalWithdrawalNumberState {
  owner_user_id: number;
  last_reserved_number: number;
  updated_at: string;
}

/**
 * Reserviert die nächste sichtbare persönliche Entnahmenummer atomar in derselben
 * IndexedDB, in der auch die lokalen Entnahmen liegen. Readwrite-Transaktionen auf
 * diesen Stores werden browserweit serialisiert, sodass zwei Tabs nicht dieselbe
 * Nummer aus demselben Ausgangsstand erhalten können. Reservierte, aber später
 * verworfene Nummern werden bewusst nicht wiederverwendet.
 */
export async function reserveNextLocalWithdrawalNumber(
  ownerUserId: number,
  confirmedFloor = 0,
): Promise<number> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(
      [WITHDRAWALS_STORE, WITHDRAWAL_NUMBER_STATE_STORE],
      "readwrite",
    );
    const withdrawalStore = transaction.objectStore(WITHDRAWALS_STORE);
    const numberStore = transaction.objectStore(WITHDRAWAL_NUMBER_STATE_STORE);
    const withdrawalsRequest = withdrawalStore.index("owner_user_id").getAll(IDBKeyRange.only(ownerUserId));
    const stateRequest = numberStore.get(ownerUserId);
    let nextNumber: number | null = null;

    const reserveWhenReady = () => {
      if (withdrawalsRequest.readyState !== "done" || stateRequest.readyState !== "done" || nextNumber !== null) return;
      const records = (withdrawalsRequest.result as LocalWithdrawalRecord[] | undefined) ?? [];
      const state = stateRequest.result as LocalWithdrawalNumberState | undefined;
      const localMax = records.reduce(
        (max, record) => Math.max(max, record.local_withdrawal_number ?? 0, record.server_withdrawal_number ?? 0),
        0,
      );
      const reservedMax = state?.last_reserved_number ?? 0;
      nextNumber = Math.max(0, confirmedFloor, localMax, reservedMax) + 1;
      numberStore.put({
        owner_user_id: ownerUserId,
        last_reserved_number: nextNumber,
        updated_at: new Date().toISOString(),
      } satisfies LocalWithdrawalNumberState);
    };

    withdrawalsRequest.onsuccess = reserveWhenReady;
    stateRequest.onsuccess = reserveWhenReady;
    withdrawalsRequest.onerror = () => reject(withdrawalsRequest.error);
    stateRequest.onerror = () => reject(stateRequest.error);
    transaction.oncomplete = () => {
      if (nextNumber === null) reject(new Error("Lokale Entnahmenummer konnte nicht reserviert werden."));
      else resolve(nextNumber);
    };
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

export const LOCAL_SYNC_LEASE_DURATION_MS = 5 * 60_000;

export function createLocalSyncLeaseOwner(context: "window" | "worker" = "window"): string {
  const random = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${context}:${random}`;
}

export async function acquireLocalWithdrawalSyncLease(
  clientUuid: string,
  leaseOwner: string,
  leaseDurationMs = LOCAL_SYNC_LEASE_DURATION_MS,
): Promise<LocalWithdrawalRecord | null> {
  const database = await openDatabase();
  const now = Date.now();
  const expiresAt = new Date(now + leaseDurationMs).toISOString();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(WITHDRAWALS_STORE, "readwrite");
    const store = transaction.objectStore(WITHDRAWALS_STORE);
    const request = store.get(clientUuid);
    let leased: LocalWithdrawalRecord | null = null;

    request.onsuccess = () => {
      const record = request.result as LocalWithdrawalRecord | undefined;
      if (!record) return;
      const currentOwner = record.sync_lease_owner ?? null;
      const currentExpiry = record.sync_lease_expires_at ? Date.parse(record.sync_lease_expires_at) : Number.NaN;
      const leaseActive = Boolean(currentOwner && Number.isFinite(currentExpiry) && currentExpiry > now);
      if (leaseActive && currentOwner !== leaseOwner) return;

      leased = {
        ...record,
        sync_lease_owner: leaseOwner,
        sync_lease_expires_at: expiresAt,
        sync_lease_updated_at: new Date(now).toISOString(),
      };
      store.put(leased);
    };
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => resolve(leased);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

export async function renewLocalWithdrawalSyncLease(
  clientUuid: string,
  leaseOwner: string,
  leaseDurationMs = LOCAL_SYNC_LEASE_DURATION_MS,
): Promise<boolean> {
  const database = await openDatabase();
  const now = Date.now();
  const expiresAt = new Date(now + leaseDurationMs).toISOString();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(WITHDRAWALS_STORE, "readwrite");
    const store = transaction.objectStore(WITHDRAWALS_STORE);
    const request = store.get(clientUuid);
    let renewed = false;

    request.onsuccess = () => {
      const record = request.result as LocalWithdrawalRecord | undefined;
      if (!record || record.sync_lease_owner !== leaseOwner) return;
      store.put({
        ...record,
        sync_lease_expires_at: expiresAt,
        sync_lease_updated_at: new Date(now).toISOString(),
      });
      renewed = true;
    };
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => resolve(renewed);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

export async function releaseLocalWithdrawalSyncLease(
  clientUuid: string,
  leaseOwner: string,
): Promise<void> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(WITHDRAWALS_STORE, "readwrite");
    const store = transaction.objectStore(WITHDRAWALS_STORE);
    const request = store.get(clientUuid);

    request.onsuccess = () => {
      const record = request.result as LocalWithdrawalRecord | undefined;
      if (!record || record.sync_lease_owner !== leaseOwner) return;
      store.put({
        ...record,
        sync_lease_owner: null,
        sync_lease_expires_at: null,
        sync_lease_updated_at: new Date().toISOString(),
      });
    };
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

export async function saveLocalWithdrawal(
  record: LocalWithdrawalRecord
): Promise<void> {
  const database =
    await openDatabase();

  return new Promise(
    (
      resolve,
      reject
    ) => {
      const transaction =
        database.transaction(
          WITHDRAWALS_STORE,
          "readwrite"
        );

      const store =
        transaction.objectStore(
          WITHDRAWALS_STORE
        );

      // Beliebige UI-/Persistenzpfade dürfen einen aktiven Sync-Lease nicht mit
      // einer älteren Record-Kopie entfernen. Freigabe erfolgt ausschließlich
      // über releaseLocalWithdrawalSyncLease().
      const currentRequest = store.get(record.client_uuid);
      currentRequest.onsuccess = () => {
        const current = currentRequest.result as LocalWithdrawalRecord | undefined;
        store.put(withPreservedActiveSyncLease(current, record));
      };
      currentRequest.onerror = () => reject(currentRequest.error);

      transaction.oncomplete =
        () => {
          resolve();
        };

      transaction.onerror =
        () => {
          reject(
            transaction.error
          );
        };

      transaction.onabort =
        () => {
          reject(
            transaction.error
          );
        };
    }
  );
}


/**
 * Echte IndexedDB-CAS-Operation für fachliche Window-Writes.
 *
 * Der Vergleich und das Schreiben laufen innerhalb derselben readwrite-
 * Transaktion. Damit können zwei Tabs nicht mehr beide Revision N lesen und
 * anschließend unabhängig Revision N+1 veröffentlichen. Ein stale Writer
 * erhält stattdessen einen LocalWithdrawalRevisionConflictError.
 *
 * Der Updater entscheidet bewusst, ob content_revision erhöht wird: fachliche
 * Änderungen müssen N+1 schreiben; technische Artefakt-/Sync-Metadaten dürfen
 * dieselbe Revision beibehalten, sofern sie exakt gegen N gegated wurden.
 */
export async function compareAndSwapLocalWithdrawal(
  clientUuid: string,
  expectedRevision: number,
  updater: (current: LocalWithdrawalRecord | null) => LocalWithdrawalRecord | null,
): Promise<LocalWithdrawalRecord | null> {
  const database = await openDatabase();
  const expected = Math.max(0, expectedRevision);

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(WITHDRAWALS_STORE, "readwrite");
    const store = transaction.objectStore(WITHDRAWALS_STORE);
    const request = store.get(clientUuid);
    let result: LocalWithdrawalRecord | null = null;

    request.onsuccess = () => {
      const current = (request.result as LocalWithdrawalRecord | undefined) ?? null;
      const actual = Math.max(0, current?.content_revision ?? 0);
      if (actual !== expected) {
        transaction.abort();
        reject(new LocalWithdrawalRevisionConflictError(clientUuid, expected, actual));
        return;
      }

      const next = updater(current);
      if (!next) {
        result = current;
        return;
      }
      result = withPreservedActiveSyncLease(current ?? undefined, next);
      store.put(result);
    };
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => {
      if (transaction.error) reject(transaction.error);
    };
    transaction.onabort = () => {
      // CAS-Konflikte werden oben bereits mit der fachlich aussagekräftigen
      // RevisionConflict-Exception rejected. Nur echte IDB-Abbrüche ergänzen.
      if (transaction.error) reject(transaction.error);
    };
  });
}


export async function updateLocalWithdrawalAtomically(
  clientUuid: string,
  updater: (current: LocalWithdrawalRecord) => LocalWithdrawalRecord | null,
): Promise<LocalWithdrawalRecord | null> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(WITHDRAWALS_STORE, "readwrite");
    const store = transaction.objectStore(WITHDRAWALS_STORE);
    const request = store.get(clientUuid);
    let result: LocalWithdrawalRecord | null = null;

    request.onsuccess = () => {
      const current = request.result as LocalWithdrawalRecord | undefined;
      if (!current) return;
      const next = updater(current);
      if (!next) {
        result = current;
        return;
      }
      result = withPreservedActiveSyncLease(current, next);
      store.put(result);
    };
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}


export async function getLocalWithdrawal(
  clientUuid: string
): Promise<
  LocalWithdrawalRecord | null
> {
  const database =
    await openDatabase();

  return new Promise(
    (
      resolve,
      reject
    ) => {
      const transaction =
        database.transaction(
          WITHDRAWALS_STORE,
          "readonly"
        );

      const store =
        transaction.objectStore(
          WITHDRAWALS_STORE
        );

      const request =
        store.get(
          clientUuid
        );

      request.onsuccess =
        () => {
          resolve(
            request.result ??
              null
          );
        };

      request.onerror =
        () => {
          reject(
            request.error
          );
        };
    }
  );
}


export async function getLocalWithdrawalsByOwner(ownerUserId: number): Promise<LocalWithdrawalRecord[]> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(WITHDRAWALS_STORE, "readonly");
    const store = transaction.objectStore(WITHDRAWALS_STORE);
    const request = store.index("owner_user_id").getAll(IDBKeyRange.only(ownerUserId));
    request.onsuccess = () => resolve(request.result ?? []);
    request.onerror = () => reject(request.error);
  });
}


export async function getAllLocalWithdrawals():
  Promise<
    LocalWithdrawalRecord[]
  > {
  const database =
    await openDatabase();

  return new Promise(
    (
      resolve,
      reject
    ) => {
      const transaction =
        database.transaction(
          WITHDRAWALS_STORE,
          "readonly"
        );

      const store =
        transaction.objectStore(
          WITHDRAWALS_STORE
        );

      const request =
        store.getAll();

      request.onsuccess =
        () => {
          resolve(
            request.result
          );
        };

      request.onerror =
        () => {
          reject(
            request.error
          );
        };
    }
  );
}


export async function getPendingLocalWithdrawals():
  Promise<
    LocalWithdrawalRecord[]
  > {
  const database =
    await openDatabase();

  return new Promise(
    (
      resolve,
      reject
    ) => {
      const transaction =
        database.transaction(
          WITHDRAWALS_STORE,
          "readonly"
        );

      const store =
        transaction.objectStore(
          WITHDRAWALS_STORE
        );

      const index =
        store.index(
          "sync_status"
        );

      const pendingRequest =
        index.getAll(
          "pending"
        );

      const errorRequest =
        index.getAll(
          "error"
        );

      // Ein Browser-/App-Abbruch kann einen Datensatz im Zustand "syncing"
      // zurücklassen. Diese Vorgänge müssen beim nächsten Queue-Lauf ebenfalls
      // wieder aufgenommen werden, sonst entsteht ein Dead-State ohne Retry.
      const syncingRequest =
        index.getAll(
          "syncing"
        );

      let pending:
        LocalWithdrawalRecord[] =
        [];

      let errors:
        LocalWithdrawalRecord[] =
        [];

      let syncing:
        LocalWithdrawalRecord[] =
        [];

      let pendingDone =
        false;

      let errorsDone =
        false;

      let syncingDone =
        false;

      const finish = () => {
        if (
          pendingDone &&
          errorsDone &&
          syncingDone
        ) {
          resolve([
            ...pending,
            ...errors,
            ...syncing,
          ]);
        }
      };

      pendingRequest.onsuccess =
        () => {
          pending =
            pendingRequest.result;

          pendingDone = true;

          finish();
        };

      errorRequest.onsuccess =
        () => {
          errors =
            errorRequest.result;

          errorsDone = true;

          finish();
        };

      syncingRequest.onsuccess =
        () => {
          syncing =
            syncingRequest.result;

          syncingDone = true;

          finish();
        };

      pendingRequest.onerror =
        () => {
          reject(
            pendingRequest.error
          );
        };

      errorRequest.onerror =
        () => {
          reject(
            errorRequest.error
          );
        };

      syncingRequest.onerror =
        () => {
          reject(
            syncingRequest.error
          );
        };
    }
  );
}


export async function deleteLocalWithdrawal(
  clientUuid: string
): Promise<void> {
  const database =
    await openDatabase();

  return new Promise(
    (
      resolve,
      reject
    ) => {
      const transaction =
        database.transaction(
          WITHDRAWALS_STORE,
          "readwrite"
        );

      transaction
        .objectStore(
          WITHDRAWALS_STORE
        )
        .delete(
          clientUuid
        );

      transaction.oncomplete =
        () => {
          resolve();
        };

      transaction.onerror =
        () => {
          reject(
            transaction.error
          );
        };
    }
  );
}

export async function saveRecoverySnapshot(record: LocalWithdrawalRecord): Promise<void> {
  if (record.record_type === "draft") return;
  const database = await openDatabase();

  // Die Recovery-Kopie ist ausschließlich eine geschützte Dokument-/Nutzdatenkopie.
  // Sie ist KEIN zweiter Queue-Eintrag und führt deshalb keinen eigenen Syncstatus,
  // Versuchszähler oder Fehlerzustand. Der operative Zustand lebt nur im Hauptspeicher.
  const snapshot: LocalWithdrawalRecord = {
    ...record,
    sync_status: "pending",
    sync_attempt_count: 0,
    last_sync_attempt_at: null,
    next_sync_attempt_at: null,
    last_error: null,
    synced_at: null,
  };

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(RECOVERY_STORE, "readwrite");
    transaction.objectStore(RECOVERY_STORE).put(snapshot);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}


export async function getRecoverySnapshot(clientUuid: string): Promise<LocalWithdrawalRecord | null> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(RECOVERY_STORE, "readonly");
    const request = transaction.objectStore(RECOVERY_STORE).get(clientUuid);
    request.onsuccess = () => resolve(request.result ?? null);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllRecoverySnapshots(): Promise<LocalWithdrawalRecord[]> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(RECOVERY_STORE, "readonly");
    const request = transaction.objectStore(RECOVERY_STORE).getAll();
    request.onsuccess = () => resolve(request.result ?? []);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteRecoverySnapshot(clientUuid: string): Promise<void> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(RECOVERY_STORE, "readwrite");
    transaction.objectStore(RECOVERY_STORE).delete(clientUuid);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}
